import { useMemo, useState } from "react";

import {
  downloadAuthenticatedCsv,
  downloadAuthenticatedTextFile,
} from "@/utils/downloadAuthenticatedCsv";
import { getErrorMessage } from "@/utils/errorHandling";
import {
  VNextButton,
  VNextCard,
  VNextDialog,
  VNextField,
  VNextInput,
  VNextKeyValueList,
  VNextNotice,
} from "@/vnext/primitives";
import { SettingsWorkspaceChrome } from "@/vnext/routes/settings/SettingsWorkspaceChrome";

import "@/vnext/routes/settings/csv-exports/csvExportsMake.css";

const DEFAULT_DAYS = 365;
const MIN_DAYS = 1;
const MAX_DAYS = 3650;

const AMY_SIGNUP_CSV_INCLUDED_CATEGORIES =
  "Business and organisation identifiers, current firm or partner context, link status, plan and subscription context, and review flags.";

const buildAmySignupCsvUrl = (days: number): string => {
  const params = new URLSearchParams({ days: String(days) });
  return `${import.meta.env.VITE_API_URL}/admin/v2/exports/amy-signups/export.csv?${params.toString()}`;
};

const buildAmySignupScriptUrl = (days: number): string => {
  const params = new URLSearchParams({ days: String(days) });
  return `${import.meta.env.VITE_API_URL}/admin/v2/exports/amy-signups/script.mjs?${params.toString()}`;
};

const parseDaysInput = (value: string): number | null => {
  if (!/^\d+$/.test(value.trim())) {
    return null;
  }

  const parsed = Number(value);
  if (!Number.isFinite(parsed)) {
    return null;
  }

  const normalized = Math.floor(parsed);
  if (normalized < MIN_DAYS || normalized > MAX_DAYS) {
    return null;
  }

  return normalized;
};

export default function CsvExportsRoute() {
  const [daysInput, setDaysInput] = useState(String(DEFAULT_DAYS));
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const [activeDownload, setActiveDownload] = useState<"csv" | "script" | null>(
    null,
  );
  const [csvExportReviewDays, setCsvExportReviewDays] = useState<number | null>(
    null,
  );

  const parsedDays = useMemo(() => parseDaysInput(daysInput), [daysInput]);
  const daysError =
    parsedDays === null
      ? `Enter a whole number between ${MIN_DAYS} and ${MAX_DAYS}.`
      : null;

  const handleDownloadScript = async () => {
    if (parsedDays === null) {
      setDownloadError(daysError);
      return;
    }

    setDownloadError(null);
    setActiveDownload("script");

    try {
      await downloadAuthenticatedTextFile({
        url: buildAmySignupScriptUrl(parsedDays),
        fallbackFilename: `generate-amy-signup-export-${parsedDays}-days.mjs`,
        mimeType: "text/javascript;charset=utf-8",
      });
    } catch (error) {
      setDownloadError(
        getErrorMessage(
          error,
          "The helper script could not be downloaded right now.",
        ),
      );
    } finally {
      setActiveDownload(null);
    }
  };

  const handleOpenCsvExportReview = () => {
    if (parsedDays === null) {
      setDownloadError(daysError);
      return;
    }

    setDownloadError(null);
    setCsvExportReviewDays(parsedDays);
  };

  const handleCloseCsvExportReview = () => {
    if (activeDownload !== "csv") {
      setCsvExportReviewDays(null);
    }
  };

  const handleConfirmCsvDownload = async () => {
    const selectedDays = csvExportReviewDays;
    if (selectedDays === null) {
      setCsvExportReviewDays(null);
      setDownloadError("Choose a valid day range before downloading.");
      return;
    }

    setDownloadError(null);
    setActiveDownload("csv");

    try {
      await downloadAuthenticatedCsv({
        url: buildAmySignupCsvUrl(selectedDays),
        fallbackFilename: `amy-new-signups-last-${selectedDays}-days.csv`,
      });
    } catch (error) {
      setDownloadError(
        getErrorMessage(
          error,
          "The CSV export could not be downloaded right now.",
        ),
      );
    } finally {
      setCsvExportReviewDays(null);
      setActiveDownload(null);
    }
  };

  const exportSummaryItems = [
    {
      key: "main_date",
      label: "Main date used",
      value: "When the account first appeared in HelloCashflow",
    },
    {
      key: "main_split",
      label: "Main direct vs linked rule",
      value: "Uses the customer’s current firm or partner link",
    },
    {
      key: "extra_context",
      label: "Extra context columns",
      value: "Stripe and plan fields stay as supporting context only",
    },
  ];

  const buttonDisabled = activeDownload !== null;

  return (
    <SettingsWorkspaceChrome
      actions={
        <div className="csv-exports-route__toolbar">
          <VNextButton
            disabled={buttonDisabled}
            onClick={() => void handleDownloadScript()}
            tone="primary"
          >
            {activeDownload === "script"
              ? "Downloading script..."
              : "Download script"}
          </VNextButton>
          <VNextButton
            disabled={buttonDisabled}
            onClick={handleOpenCsvExportReview}
            tone="secondary"
          >
            {activeDownload === "csv" ? "Downloading CSV..." : "Download CSV"}
          </VNextButton>
        </div>
      }
      subtitle="Download repeatable exports and the helper scripts that regenerate them."
      title="CSV exports"
    >
      <div className="csv-exports-route">
        <VNextNotice tone="info">
          Use this page when Amy needs a fresh signup review export. Download
          the script first when you want the repeatable version. The CSV button
          opens a review step before the one-off file downloads.
        </VNextNotice>

        {downloadError ? (
          <VNextNotice tone="danger">{downloadError}</VNextNotice>
        ) : null}

        <div className="csv-exports-route__grid">
          <VNextCard
            subtitle="This export is designed for Amy’s signup review and keeps the main yes / no decision simple."
            title="Amy signup review export"
          >
            <div className="csv-exports-route__card-stack">
              <VNextField
                error={daysError}
                hint="Use whole days. 365 gives the last year."
                label="Days back"
              >
                <VNextInput
                  inputMode="numeric"
                  max={MAX_DAYS}
                  min={MIN_DAYS}
                  onChange={(event) => setDaysInput(event.target.value)}
                  placeholder="365"
                  step={1}
                  type="number"
                  value={daysInput}
                />
              </VNextField>

              <VNextKeyValueList items={exportSummaryItems} />
            </div>
          </VNextCard>

          <VNextCard
            subtitle="Use these notes to choose the right download before you send anything on."
            title="How to use it"
          >
            <div className="csv-exports-route__card-stack">
              <div className="csv-exports-route__note-block">
                <h3>Download script</h3>
                <p>
                  Best when you want the repeatable helper. It forwards to the
                  canonical workspace script and keeps the day range you picked
                  as the default.
                </p>
              </div>
              <div className="csv-exports-route__note-block">
                <h3>Download CSV</h3>
                <p>
                  Best when you only need the current export file now. It opens
                  a review step first and uses the same current-state signup
                  rule as the audited script.
                </p>
              </div>
              <div className="csv-exports-route__note-block">
                <h3>Important reading rule</h3>
                <p>
                  The direct vs linked split is based on who the account is
                  linked to now. Stripe details are extra context only.
                </p>
              </div>
            </div>
          </VNextCard>
        </div>

        <VNextDialog
          description="Review the selected range and included fields before downloading the CSV."
          footer={
            <>
              <VNextButton
                disabled={activeDownload === "csv"}
                onClick={handleCloseCsvExportReview}
                tone="quiet"
              >
                Cancel
              </VNextButton>
              <VNextButton
                disabled={activeDownload === "csv"}
                onClick={() => void handleConfirmCsvDownload()}
                tone="primary"
              >
                {activeDownload === "csv" ? "Downloading..." : "Download CSV"}
              </VNextButton>
            </>
          }
          onClose={handleCloseCsvExportReview}
          open={csvExportReviewDays !== null}
          title="Review Amy signup CSV export"
        >
          <div className="vnext-stack">
            <VNextNotice tone="warning">
              This CSV can include sensitive identifiers and customer account
              context. Download it only when the signup review needs these
              fields.
            </VNextNotice>
            <VNextKeyValueList
              items={[
                {
                  key: "export",
                  label: "Export",
                  value: "Amy signup review CSV",
                },
                {
                  key: "date_range",
                  label: "Date range",
                  value: `Last ${csvExportReviewDays ?? parsedDays ?? DEFAULT_DAYS} days`,
                },
                {
                  key: "included_categories",
                  label: "CSV includes",
                  value: AMY_SIGNUP_CSV_INCLUDED_CATEGORIES,
                },
                {
                  key: "reading_rule",
                  label: "Reading rule",
                  value:
                    "The direct vs linked split uses the account's current firm or partner link.",
                },
                {
                  key: "audit",
                  label: "Audit log",
                  value:
                    "Stores the day range, row count, and export window. It does not store raw CSV values.",
                },
              ]}
            />
          </div>
        </VNextDialog>
      </div>
    </SettingsWorkspaceChrome>
  );
}
